import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Store } from 'store/Store';
import { ActionBase } from 'store/ActionBase';
import { RegisterUserData, registerUserGenerator } from 'store/register-user/RegisterUserData';
import { RoutePath } from 'consts/RoutePath';
import { loginGenerator } from 'store/login-user/LoginUserData';
import {
	callCommonLoginV1Api,
	callCommonLoginV2Api,
	CommonLoginV1ResData,
	CommonLoginV2ResData,
} from 'requests/RequestLogin';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';

export type ResponseData = {
	v1: CommonLoginV1ResData;
	v2: CommonLoginV2ResData;
};

/**
 * 新規会員登録 登録する 処理のカスタムフック
 * 登録する実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useRegister = (
	formData: RegisterUserData,
	store: Store,
	dispatch: React.Dispatch<ActionBase>,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
) => {
	// 登録する実処理
	const [isRegisterFinished, setIsRegisterFinished] = useState(false);
	const register = async () => {
		setProcessLoading(true);
		let updateSiteCd = process.env.REACT_APP_PORTAL_SITE_CD;
		if (store.registerUserData.isV1ParamsOk && store.authorizeV1Data.scd) {
			updateSiteCd = store.authorizeV1Data.scd;
		} else if (store.registerUserData.isV2ParamsOk) {
			updateSiteCd = store.authorizeV2Data.client_id;
		}
		await registerUserGenerator(dispatch, formData, updateSiteCd);
		setIsRegisterFinished(true);
	};

	// ログイン
	const [loginError, setLoginError] = useState('');
	const [resDatas, setResDatas] = useState<ResponseData>({ v1: undefined, v2: undefined });
	const [isLoginFinished, setIsLoginFinished] = useState(false);
	useEffect(() => {
		if (!isRegisterFinished) {
			return;
		}

		const isError = store.registerUserData.actionError !== '';
		if (isError) {
			setIsRegisterFinished(false);
			history.push(RoutePath.ERROR);
			return;
		}

		const execute = async () => {
			// 認可v1のパラメータが存在する場合
			if (store.registerUserData.isV1ParamsOk) {
				const result = await callCommonLoginV1Api({
					gid: store.registerUserData.gid,
					pw: store.registerUserData.pass,
					...store.authorizeV1Data,
				});
				if (result.status !== HttpStatus.OK || !result.data) {
					setLoginError(ReactError.E9999);
				}
				const data = result.data as CommonLoginV1ResData;
				setResDatas((prev) => ({ ...prev, v1: data }));
			}
			// 認可v2のパラメータが存在する場合
			else if (store.registerUserData.isV2ParamsOk) {
				const result = await callCommonLoginV2Api({
					gid: store.registerUserData.gid,
					pw: store.registerUserData.pass,
					...store.authorizeV2Data,
				});
				if (result.status !== HttpStatus.OK || !result.data) {
					setLoginError(ReactError.E9999);
				}
				const data = result.data as CommonLoginV2ResData;
				setResDatas((prev) => ({ ...prev, v2: data }));
			} else {
				await loginGenerator(dispatch, store.registerUserData.gid, store.registerUserData.pass);
			}
			setIsLoginFinished(true);
		};
		execute();
	}, [isRegisterFinished]);

	// ページ遷移
	const history = useHistory();
	useEffect(() => {
		if (!isLoginFinished) {
			return;
		}

		const isError = !!store.loginUserData.actionError || !!loginError;
		if (isError) {
			setIsRegisterFinished(false);
			setIsLoginFinished(false);
			history.push(RoutePath.ERROR);
			return;
		}

		// 次のページへ
		history.push(RoutePath.USER_REGISTER_5, resDatas);
	}, [isLoginFinished]);

	return {
		register,
	};
};
